import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AdminSchoolYearService } from '../../services/admin/admin-school-year.service';
import {
  SchoolYearModel,
  CreateSchoolYearPayload,
  UpdateSchoolYearPayload
} from '../../models/school/school-year.model';
import { ModalService } from '../../shared/modals/modal/modal.service';
import { ErrorModel } from '../../models/shared/error.model';
import { CountryModel } from '../../models/localization-and-pricing/country.model';
import { DropdownModel } from '../../models/component/dropdown.model';

@Component({
  selector: 'app-admin-school-years',
  templateUrl: './admin-school-years.component.html',
  styleUrls: ['./admin-school-years.component.css']
})
export class AdminSchoolYearsComponent implements OnInit {

  schoolYears: SchoolYearModel[] = [];
  countryFilter: string[] = [];
  isLoading = false;

  countryOptions: DropdownModel[] = [];

  showCreateModal = false;
  showCloseModal = false;

  editingYear: SchoolYearModel = null;
  closingYear: SchoolYearModel = null;
  closeConfirmInput = '';

  formName = '';
  formCountry = '';
  formStartDate = '';
  formEndDate = '';
  formIsActive = false;

  private collapsedGroups = new Set<string>();

  constructor(
    private schoolYearSv: AdminSchoolYearService,
    private modalSv: ModalService,
    private router: Router
  ) {}

  ngOnInit() {
    const countryCodes = CountryModel.countriesForCurrentDomain();
    this.countryOptions = CountryModel.dropdownList(countryCodes);
    this.fetchYears();
  }

  fetchYears() {
    this.isLoading = true;
    this.schoolYearSv.getSchoolYears().subscribe(years => {
      this.schoolYears = years;
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
      this.modalSv.showAlertModal('Error', 'Could not load school years. Please refresh the page.');
    });
  }

  get filteredYears(): SchoolYearModel[] {
    if (!this.countryFilter || this.countryFilter.length === 0) {
      return this.schoolYears;
    }
    return this.schoolYears.filter(y => this.countryFilter.indexOf(y.country) !== -1);
  }

  setCountryFilter(keys: string[]) {
    this.countryFilter = keys;
    this.fetchYears();
  }

  get isGroupedByCountry(): boolean {
    return !this.countryFilter || this.countryFilter.length === 0;
  }

  get groupedYears(): { country: string, title: string, years: SchoolYearModel[], activeCount: number }[] {
    const groups = new Map<string, SchoolYearModel[]>();
    for (const year of this.filteredYears) {
      const key = year.country || '';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(year);
    }
    return Array.from(groups.entries())
      .map(([country, years]) => ({
        country,
        title: this.countryTitle(country),
        years,
        activeCount: years.filter(y => y.isActive && !y.isClosed).length
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  isGroupCollapsed(country: string): boolean {
    return this.collapsedGroups.has(country);
  }

  toggleGroup(country: string) {
    if (this.collapsedGroups.has(country)) {
      this.collapsedGroups.delete(country);
    } else {
      this.collapsedGroups.add(country);
    }
  }

  countryTitle(code: string): string {
    return CountryModel.title(code);
  }

  formatDate(d: string): string {
    if (!d) {
      return '—';
    }
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  openCreateModal() {
    this.editingYear = null;
    this.formName = '';
    this.formCountry = '';
    this.formStartDate = '';
    this.formEndDate = '';
    this.formIsActive = false;
    this.showCreateModal = true;
  }

  openEditModal(year: SchoolYearModel) {
    this.editingYear = year;
    this.formName = year.name;
    this.formCountry = year.country;
    this.formStartDate = this.toDateInputValue(year.startDate);
    this.formEndDate = this.toDateInputValue(year.endDate);
    this.formIsActive = year.isActive;
    this.showCreateModal = true;
  }

  toDateInputValue(d: string): string {
    if (!d) {
      return '';
    }
    return new Date(d).toISOString().substring(0, 10);
  }

  closeCreateModal() {
    this.showCreateModal = false;
    this.editingYear = null;
  }

  saveYear() {
    if (!this.formName || !this.formCountry || !this.formStartDate || !this.formEndDate) {
      this.modalSv.showAlertModal('Error', 'Please fill in all fields.');
      return;
    }
    if (this.editingYear) {
      const payload = new UpdateSchoolYearPayload();
      payload.name = this.formName;
      payload.startDate = this.formStartDate;
      payload.endDate = this.formEndDate;
      payload.isActive = this.formIsActive;
      this.schoolYearSv.updateSchoolYear(this.editingYear._id, payload).subscribe(() => {
        this.closeCreateModal();
        this.fetchYears();
      }, (err: ErrorModel) => {
        this.modalSv.showTranslatedErrorModal(err);
      });
    } else {
      const payload = new CreateSchoolYearPayload();
      payload.name = this.formName;
      payload.country = this.formCountry;
      payload.startDate = this.formStartDate;
      payload.endDate = this.formEndDate;
      payload.isActive = this.formIsActive;
      this.schoolYearSv.createSchoolYear(payload).subscribe(() => {
        this.closeCreateModal();
        this.fetchYears();
      }, (err: ErrorModel) => {
        this.modalSv.showTranslatedErrorModal(err);
      });
    }
  }

  openCloseModal(year: SchoolYearModel) {
    this.closingYear = year;
    this.closeConfirmInput = '';
    this.showCloseModal = true;
  }

  closeCloseModal() {
    this.showCloseModal = false;
    this.closingYear = null;
  }

  get closeConfirmValid(): boolean {
    return this.closingYear && this.closeConfirmInput.trim() === this.closingYear.name;
  }

  confirmCloseYear() {
    if (!this.closeConfirmValid) {
      return;
    }
    const payload = new UpdateSchoolYearPayload();
    payload.isActive = false;
    this.schoolYearSv.updateSchoolYear(this.closingYear._id, payload).subscribe(() => {
      this.closeCloseModal();
      this.fetchYears();
    }, (err: ErrorModel) => {
      this.modalSv.showTranslatedErrorModal(err);
    });
  }

  viewDetail(year: SchoolYearModel) {
    this.router.navigate(['admin', 'schoolYears', year._id, 'detail']);
  }
}
